import React, { forwardRef } from "react";
import { Star, MoreVertical, Pencil, Trash2, ExternalLink, GripVertical } from "lucide-react";
import { getIcon } from "@/lib/iconMap";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const AppCard = forwardRef(function AppCard(
  { app, onLaunch, onEdit, onDelete, onToggleFavorite, style, dragHandleProps, isDragging, reorderable },
  ref
) {
  const Icon = getIcon(app.icon);

  return (
    <div
      ref={ref}
      style={style}
      className={`group relative animate-fade-up ${isDragging ? "z-50 opacity-80" : ""}`}
      data-testid={`app-card-${app.id}`}
    >
      <button
        onClick={() => onLaunch(app)}
        data-testid={`launch-app-${app.id}`}
        className={`w-full text-left rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-transform transition-shadow duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/50 ${isDragging ? "shadow-2xl ring-2 ring-primary/40" : ""}`}
      >
        <div className="flex items-start justify-between">
          <div className={`${app.color} flex h-14 w-14 items-center justify-center rounded-xl text-white shadow-md`}>
            <Icon className="h-7 w-7" strokeWidth={2} />
          </div>
          {app.open_mode === "browser" && (
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <h3 className="mt-4 truncate text-base font-semibold tracking-tight text-card-foreground">
          {app.name}
        </h3>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {app.url.replace(/^https?:\/\//, "")}
        </p>
      </button>

      {reorderable && (
        <button
          {...dragHandleProps}
          data-testid={`drag-handle-${app.id}`}
          className="absolute left-3 top-4 cursor-grab touch-none rounded-full p-1.5 opacity-0 transition-opacity duration-200 hover:bg-accent group-hover:opacity-100 active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <button
        onClick={() => onToggleFavorite(app)}
        data-testid={`favorite-toggle-${app.id}`}
        className="absolute right-11 top-4 rounded-full p-1.5 opacity-0 transition-opacity duration-200 hover:bg-accent group-hover:opacity-100"
        aria-label="Toggle favorite"
      >
        <Star className={`h-4 w-4 ${app.favorite ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`app-menu-${app.id}`}
            className="absolute right-3 top-4 rounded-full p-1.5 opacity-0 transition-opacity duration-200 hover:bg-accent group-hover:opacity-100"
            aria-label="App options"
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={() => onEdit(app)} data-testid={`edit-app-${app.id}`}>
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDelete(app)}
            className="text-destructive focus:text-destructive"
            data-testid={`delete-app-${app.id}`}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {app.favorite && (
        <div className="pointer-events-none absolute right-11 top-4 opacity-100 group-hover:opacity-0">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
        </div>
      )}
    </div>
  );
});
